import type {
  CSVValidationResult,
  AccountingConnection,
  AccountingAsset,
  ImportResult,
  FilterOptions,
  Asset,
  Transaction,
  TransactionFilterOptions,
  TransactionOverride,
} from '../types/asset-import.types';
import { TransactionType, TransactionSource } from '../types/asset-import.types';
import type { AgentSessionStatus } from '@/types/agent.types';

export interface FetchTransactionsWithAgentResult {
  transactions: Transaction[];
  totalCount: number;
  eligibleCount: number;
  alreadyUsedCount: number;
  excludedCount: number;
  agentSessionId?: string;
  agentStatus?: AgentSessionStatus;
}

interface BackendTransaction {
  primaryKey?: string;
  id?: string;
  transactionType?: string;
  transactionSource?: string;
  transactionDate?: string;
  reference?: string;
  description?: string;
  detail?: string;
  netValue?: number;
  grossValue?: number;
  vatAmount?: number;
  supplierName?: string;
  supplierCode?: string;
  analysisCode?: string;
  nominalAccount?: string;
  categoryCode?: string;
  costCentreCode?: string;
  projectCode?: string;
  quantity?: number;
  serialNumber?: string;
  status?: string;
  exclusionReason?: string;
  canCreateAsset?: boolean;
}
import { parseCSV } from '../utils/csv-parser';
import { validateAssetRow } from '../utils/asset-validator';
import { getCategoryNames } from '../config/categories';
import { mockIntegrations } from '@/types/integrations';
import { authFetch, getAuthConfig } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';

class AssetImportService {
  private baseUrl = '/api/assets/import';

  async uploadCSV(file: File): Promise<CSVValidationResult> {
    try {
      // Parse CSV
      const parseResult = await parseCSV(file);
      
      if (parseResult.errors.length > 0) {
        return {
          valid: false,
          totalRows: 0,
          validRows: 0,
          errorCount: parseResult.errors.length,
          errors: parseResult.errors,
          validAssets: [],
          previewAssets: []
        };
      }

      // Validate each row
      const validCategories = getCategoryNames();
      const existingAssetCodes: string[] = []; // TODO: Fetch from API
      const allErrors: any[] = [];
      const validAssets: Asset[] = [];

      parseResult.data.forEach((row, index) => {
        const rowErrors = validateAssetRow(row);
        
        if (rowErrors.length > 0) {
          allErrors.push(...rowErrors);
        } else {
          // Convert to Asset
          validAssets.push({
            id: `temp-${index}`,
            assetCode: row['Asset Code'],
            description: row['Description'],
            category: row['Category'],
            boughtValue: parseFloat(row['Bought Value'].replace(/[£,]/g, '')),
            boughtDate: row['Bought Date'],
            supplier: row['Supplier'],
            serialNumber: row['Serial Number'],
            location: row['Location'],
            notes: row['Notes']
          });
        }
      });

      return {
        valid: allErrors.length === 0,
        totalRows: parseResult.data.length,
        validRows: validAssets.length,
        errorCount: allErrors.length,
        errors: allErrors,
        validAssets: validAssets,
        previewAssets: validAssets.slice(0, 5)
      };
    } catch (error) {
      throw new Error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async downloadCSVTemplate(): Promise<Blob> {
    // For now, return a mock blob
    // In production, this would call the API
    const { generateCSVTemplate } = await import('../utils/csv-parser');
    const csv = generateCSVTemplate();
    return new Blob([csv], { type: 'text/csv' });
  }

  async importValidAssets(assetIds: string[]): Promise<ImportResult> {
    // Mock implementation
    // In production, this would call: POST /api/assets/import/csv/import
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          importedCount: assetIds.length,
          failedCount: 0
        });
      }, 1500);
    });
  }

  async exportValidationErrors(errors: any[]): Promise<Blob> {
    // Create CSV from errors
    const headers = ['Row', 'Field', 'Error', 'Value'];
    let csv = headers.join(',') + '\n';
    
    errors.forEach(error => {
      const row = [
        error.row,
        error.field,
        `"${error.error}"`,
        error.value || ''
      ];
      csv += row.join(',') + '\n';
    });

    return new Blob([csv], { type: 'text/csv' });
  }

  async getAccountingConnection(): Promise<AccountingConnection> {
    // Mock implementation
    // In production, this would call: GET /api/assets/import/accounting/connection
    return new Promise((resolve) => {
      setTimeout(() => {
        const systemType = 'access-financials';
        const integration = mockIntegrations.find(i => i.id === systemType);
        
        resolve({
          id: '1',
          systemName: integration?.name || 'Access Financials',
          systemType: systemType,
          isConnected: true,
          lastSyncDate: new Date().toISOString(),
          logoUrl: integration?.logo
        });
      }, 500);
    });
  }

  async fetchAccountingAssets(filters: FilterOptions): Promise<AccountingAsset[]> {
    // Mock implementation
    // In production, this would call: POST /api/assets/import/accounting/fetch
    return new Promise((resolve) => {
      setTimeout(() => {
        // Realistic asset descriptions and details
        const assetTemplates = [
          // Computer Equipment
          { name: 'Dell Latitude 5420 Laptop', category: 'Computer Equipment', manufacturer: 'Dell', model: 'Latitude 5420', priceRange: [800, 1500] },
          { name: 'HP EliteBook 840 G8', category: 'Computer Equipment', manufacturer: 'HP', model: 'EliteBook 840 G8', priceRange: [900, 1600] },
          { name: 'MacBook Pro 14" M2', category: 'Computer Equipment', manufacturer: 'Apple', model: 'MacBook Pro 14"', priceRange: [1800, 2500] },
          { name: 'Dell UltraSharp 27" Monitor', category: 'Computer Equipment', manufacturer: 'Dell', model: 'U2722DE', priceRange: [400, 600] },
          { name: 'HP LaserJet Pro MFP M428fdw', category: 'Computer Equipment', manufacturer: 'HP', model: 'M428fdw', priceRange: [500, 800] },
          { name: 'Lenovo ThinkCentre Desktop', category: 'Computer Equipment', manufacturer: 'Lenovo', model: 'ThinkCentre M720', priceRange: [700, 1200] },
          { name: 'Microsoft Surface Pro 9', category: 'Computer Equipment', manufacturer: 'Microsoft', model: 'Surface Pro 9', priceRange: [1000, 1800] },
          { name: 'Samsung 32" Curved Monitor', category: 'Computer Equipment', manufacturer: 'Samsung', model: 'C32F391', priceRange: [300, 500] },
          
          // Furniture & Fixtures
          { name: 'Herman Miller Aeron Chair', category: 'Furniture & Fixtures', manufacturer: 'Herman Miller', model: 'Aeron', priceRange: [1200, 1800] },
          { name: 'Steelcase Leap V2 Chair', category: 'Furniture & Fixtures', manufacturer: 'Steelcase', model: 'Leap V2', priceRange: [900, 1400] },
          { name: 'Standing Desk Electric Adjustable', category: 'Furniture & Fixtures', manufacturer: 'FlexiSpot', model: 'E7', priceRange: [500, 800] },
          { name: 'Executive Office Desk Oak', category: 'Furniture & Fixtures', manufacturer: 'Office Furniture Ltd', model: 'Executive Oak', priceRange: [800, 1500] },
          { name: 'Meeting Room Table 8-Seater', category: 'Furniture & Fixtures', manufacturer: 'Office Supplies Ltd', model: 'Conference 8', priceRange: [1200, 2000] },
          { name: 'Filing Cabinet 4-Drawer Steel', category: 'Furniture & Fixtures', manufacturer: 'Bisley', model: 'BS4E', priceRange: [300, 500] },
          { name: 'Reception Desk L-Shaped', category: 'Furniture & Fixtures', manufacturer: 'Reception Furniture Co', model: 'L-Shape Pro', priceRange: [2000, 3500] },
          
          // Vehicles
          { name: 'Ford Transit Custom Van', category: 'Vehicles', manufacturer: 'Ford', model: 'Transit Custom 320', priceRange: [25000, 35000] },
          { name: 'Volkswagen Transporter T6.1', category: 'Vehicles', manufacturer: 'Volkswagen', model: 'Transporter T6.1', priceRange: [28000, 38000] },
          { name: 'Mercedes-Benz Sprinter', category: 'Vehicles', manufacturer: 'Mercedes-Benz', model: 'Sprinter 314 CDI', priceRange: [32000, 42000] },
          { name: 'Toyota Hilux Double Cab', category: 'Vehicles', manufacturer: 'Toyota', model: 'Hilux Invincible', priceRange: [30000, 40000] },
          { name: 'Nissan Leaf Electric', category: 'Vehicles', manufacturer: 'Nissan', model: 'Leaf e+ Tekna', priceRange: [28000, 35000] },
          
          // Machinery
          { name: 'Forklift Truck 2.5 Tonne', category: 'Machinery', manufacturer: 'Toyota Material Handling', model: '8FBN25', priceRange: [18000, 25000] },
          { name: 'Pallet Jack Electric', category: 'Machinery', manufacturer: 'Crown', model: 'PE4500', priceRange: [8000, 12000] },
          { name: 'Industrial Air Compressor', category: 'Machinery', manufacturer: 'Atlas Copco', model: 'GA 15', priceRange: [12000, 18000] },
          { name: 'CNC Milling Machine', category: 'Machinery', manufacturer: 'Haas', model: 'VF-2SS', priceRange: [45000, 65000] },
          { name: 'Packaging Machine Automatic', category: 'Machinery', manufacturer: 'Bosch', model: 'SVE 2520 D', priceRange: [35000, 50000] },
          
          // Office Equipment
          { name: 'Cisco IP Phone 8841', category: 'Office Equipment', manufacturer: 'Cisco', model: '8841', priceRange: [200, 350] },
          { name: 'Polycom Conference Phone', category: 'Office Equipment', manufacturer: 'Polycom', model: 'SoundStation IP 6000', priceRange: [400, 600] },
          { name: 'Shredder Cross-Cut Heavy Duty', category: 'Office Equipment', manufacturer: 'Fellowes', model: 'Powershred 99Ci', priceRange: [300, 500] },
          { name: 'Laminator A3 Professional', category: 'Office Equipment', manufacturer: 'GBC', model: 'Fusion 3000L', priceRange: [250, 400] },
          { name: 'Projector Full HD 4000 Lumens', category: 'Office Equipment', manufacturer: 'Epson', model: 'EB-2250U', priceRange: [1500, 2500] },
        ];

        const suppliers = [
          'Dell Technologies UK Ltd',
          'HP UK Ltd',
          'Apple UK',
          'Office Supplies Ltd',
          'Ford Dealer UK',
          'Volkswagen Commercial Vehicles',
          'Mercedes-Benz UK',
          'Toyota GB',
          'Nissan Motor GB',
          'Office Furniture Ltd',
          'Herman Miller UK',
          'Steelcase UK',
          'Machinery Direct Ltd',
          'Atlas Copco UK',
          'Bosch UK',
          'Cisco Systems UK',
          'Epson UK',
          'Tech Supplies Ltd',
          'Equipment Solutions UK',
          'Industrial Equipment Co'
        ];

        const locations = [
          'Head Office - London',
          'Distribution Centre - Birmingham',
          'Regional Office - Manchester',
          'Warehouse - Leeds',
          'Branch Office - Bristol',
          'Manufacturing Plant - Sheffield',
          'Sales Office - Edinburgh',
          'Service Centre - Cardiff'
        ];

        // Generate 47 realistic assets
        const mockAssets: AccountingAsset[] = [];
        
        // Add some already imported assets (about 10%)
        const alreadyImportedCount = 5;
        
        for (let i = 0; i < 47; i++) {
          const template = assetTemplates[i % assetTemplates.length];
          const isAlreadyImported = i < alreadyImportedCount;
          
          // Generate realistic purchase date (within last 3 years)
          const daysAgo = Math.floor(Math.random() * 1095); // 3 years
          const purchaseDate = new Date();
          purchaseDate.setDate(purchaseDate.getDate() - daysAgo);
          
          // Generate price within template range
          const [minPrice, maxPrice] = template.priceRange;
          const price = Math.floor(Math.random() * (maxPrice - minPrice) + minPrice);
          
          // Generate serial number (80% have serial numbers)
          const hasSerial = Math.random() > 0.2;
          const serialNumber = hasSerial 
            ? `${template.manufacturer.substring(0, 3).toUpperCase()}${String(i + 1000).padStart(6, '0')}` 
            : undefined;
          
          // Select random supplier (prefer manufacturer if available)
          const supplier = Math.random() > 0.3 
            ? suppliers.find(s => s.includes(template.manufacturer)) || suppliers[i % suppliers.length]
            : suppliers[i % suppliers.length];
          
          // Select random location
          const location = locations[i % locations.length];
          
          // Generate invoice reference
          const invoiceRef = `INV-${new Date(purchaseDate).getFullYear()}-${String(i + 1).padStart(4, '0')}`;
          
          // Generate PO number
          const poNumber = `PO-${String(i + 10000).padStart(6, '0')}`;

          mockAssets.push({
            id: `${i + 1}`,
            assetCode: `FA${String(i + 1).padStart(4, '0')}`,
            description: template.name,
            category: template.category,
            purchasePrice: price,
            purchaseDate: purchaseDate.toISOString().split('T')[0],
            supplier: supplier,
            serialNumber: serialNumber,
            manufacturer: template.manufacturer,
            model: template.model,
            location: location,
            invoiceReference: invoiceRef,
            purchaseOrderNumber: poNumber,
            status: isAlreadyImported ? 'already-imported' : 'new',
            notes: isAlreadyImported ? 'This asset has already been imported' : undefined
          });
        }

        resolve(mockAssets);
      }, 2000);
    });
  }

  async importAccountingAssets(assetIds: string[]): Promise<ImportResult> {
    // Mock implementation
    // In production, this would call: POST /api/assets/import/accounting/import
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          importedCount: assetIds.length,
          failedCount: 0
        });
      }, 1500);
    });
  }

  async fetchTransactions(
    filters: TransactionFilterOptions,
    enableAgentProcessing: boolean = false
  ): Promise<FetchTransactionsWithAgentResult> {
    const authConfig = getAuthConfig();
    const financialsInstanceId = authConfig.financialsInstanceId ?? authConfig.instanceId ?? '';

    if (!financialsInstanceId) {
      console.warn('[AssetImportService] No financialsInstanceId configured, returning empty transactions');
      return {
        transactions: [],
        totalCount: 0,
        eligibleCount: 0,
        alreadyUsedCount: 0,
        excludedCount: 0,
      };
    }

    try {
      const apiUrl = getApiUrl();
      const params = new URLSearchParams();
      params.append('financialsInstanceId', financialsInstanceId);
      
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom.toISOString());
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo.toISOString());
      }
      if (filters.category) {
        params.append('categoryCode', filters.category);
      }
      if (filters.supplier) {
        params.append('supplierCode', filters.supplier);
      }
      if (filters.minValue !== undefined) {
        params.append('minValue', filters.minValue.toString());
      }
      if (filters.maxValue !== undefined) {
        params.append('maxValue', filters.maxValue.toString());
      }
      if (enableAgentProcessing) {
        params.append('enableAgentProcessing', 'true');
      }

      const response = await authFetch(
        `${apiUrl}/api/assets/financials/transactions/fetch?${params.toString()}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message ?? `Failed to fetch transactions: ${response.status}`);
      }

      const data = await response.json();
      
      const transactions = (data.transactions ?? []).map((t: BackendTransaction) => this.mapBackendTransactionToFrontend(t));
      
      return {
        transactions,
        totalCount: data.totalCount ?? transactions.length,
        eligibleCount: data.eligibleCount ?? 0,
        alreadyUsedCount: data.alreadyUsedCount ?? 0,
        excludedCount: data.excludedCount ?? 0,
        agentSessionId: data.agentSessionId,
        agentStatus: data.agentStatus,
      };
    } catch (error) {
      console.error('[AssetImportService] Error fetching transactions:', error);
      throw error;
    }
  }

  async createAssetsFromTransactions(
    transactionIds: string[], 
    useGrossValue: boolean = false,
    transactionOverrides?: Record<string, TransactionOverride>
  ): Promise<ImportResult> {
    const authConfig = getAuthConfig();
    const financialsInstanceId = authConfig.financialsInstanceId ?? authConfig.instanceId ?? '';

    if (!financialsInstanceId) {
      return {
        success: false,
        importedCount: 0,
        failedCount: transactionIds.length,
        message: 'No financialsInstanceId configured'
      };
    }

    try {
      const apiUrl = getApiUrl();
      const params = new URLSearchParams();
      params.append('financialsInstanceId', financialsInstanceId);

      const response = await authFetch(
        `${apiUrl}/api/assets/financials/transactions/import?${params.toString()}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transactionIds,
            useGrossValue,
            transactionOverrides,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create assets: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: data.success,
        importedCount: data.assetsCreated || 0,
        failedCount: data.errors?.length || 0,
        skippedCount: data.assetsUpdated || 0,
        message: data.success 
          ? `Successfully created ${data.assetsCreated} assets from transactions`
          : 'Some transactions failed to import',
        errors: data.errors?.map((e: any) => e.errorMessage) || []
      };
    } catch (error) {
      console.error('[AssetImportService] Error creating assets from transactions:', error);
      return {
        success: false,
        importedCount: 0,
        failedCount: transactionIds.length,
        message: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private mapBackendTransactionToFrontend(t: BackendTransaction): Transaction {
    const sourceMap: Record<string, TransactionSource> = {
      'PurchaseLedger': TransactionSource.PurchaseLedger,
      'NominalLedger': TransactionSource.NominalLedger,
      'Costing': TransactionSource.Costing,
    };

    const typeMap: Record<string, TransactionType> = {
      'Invoice': TransactionType.PurchaseInvoice,
      'CreditNote': TransactionType.PurchaseCreditNote,
      'Debit': TransactionType.NominalDebit,
      'Credit': TransactionType.NominalCredit,
      'Timesheet': TransactionType.CostingTimesheet,
      'Miscellaneous': TransactionType.CostingMiscellaneous,
    };

    const statusMap: Record<string, 'eligible' | 'already-used' | 'excluded'> = {
      'Eligible': 'eligible',
      'AlreadyUsed': 'already-used',
      'Excluded': 'excluded',
    };

    return {
      id: t.primaryKey ?? t.id ?? '',
      transactionType: typeMap[t.transactionType ?? ''] ?? TransactionType.PurchaseInvoice,
      transactionSource: sourceMap[t.transactionSource ?? ''] ?? TransactionSource.PurchaseLedger,
      transactionDate: t.transactionDate ? new Date(t.transactionDate).toISOString().split('T')[0] : '',
      transactionReference: t.reference ?? '',
      description: t.description ?? t.detail ?? '',
      netValue: t.netValue ?? 0,
      grossValue: t.grossValue ?? 0,
      vatAmount: t.vatAmount ?? 0,
      supplier: t.supplierName ?? t.supplierCode,
      analysisCode: t.analysisCode,
      nominalAccount: t.nominalAccount,
      category: t.categoryCode,
      costCentre: t.costCentreCode,
      project: t.projectCode,
      quantity: t.quantity,
      serialNumber: t.serialNumber,
      status: statusMap[t.status ?? ''] ?? 'eligible',
      exclusionReason: t.exclusionReason,
      canCreateAsset: t.canCreateAsset ?? true,
    };
  }
}

export const assetImportService = new AssetImportService();
